import {
  FlagbagFilterStatus,
  FlagbagServerStatus,
  StatusCode,
} from "services/api/flagbags/types";

const COLLECTED_SERVER_STATUSES: StatusCode[] = [
  StatusCode.COLLECTED,
  StatusCode.BANK,
  StatusCode.COLLECTED_EMPTY,
  StatusCode.COLLECTED_OTHER,
];

const NOT_RECEIVED_SERVER_STATUSES: StatusCode[] = [
  StatusCode.DISTRIBUTED,
  StatusCode.DISTRIBUTED_OTHER,
  StatusCode.LOSS,
  StatusCode.LATE,
];

const ALLOWED_STATUS_MAP: Record<StatusCode, StatusCode[]> = {
  [StatusCode.NOT_ASSIGN]: [StatusCode.ASSIGNED],
  [StatusCode.ASSIGNED]: [StatusCode.DISTRIBUTED, StatusCode.DISTRIBUTED_OTHER],
  [StatusCode.DISTRIBUTED]: [
    StatusCode.DISTRIBUTED_OTHER,
    StatusCode.COLLECTED,
    StatusCode.BANK,
    StatusCode.COLLECTED_EMPTY,
    StatusCode.COLLECTED_OTHER,
    StatusCode.LOSS,
    StatusCode.LATE,
  ],
  [StatusCode.DISTRIBUTED_OTHER]: [
    StatusCode.DISTRIBUTED,
    StatusCode.COLLECTED,
    StatusCode.BANK,
    StatusCode.COLLECTED_EMPTY,
    StatusCode.COLLECTED_OTHER,
    StatusCode.LOSS,
    StatusCode.LATE,
  ],
  [StatusCode.LOSS]: [
    StatusCode.LATE,
    StatusCode.COLLECTED,
    StatusCode.BANK,
    StatusCode.COLLECTED_EMPTY,
    StatusCode.COLLECTED_OTHER,
  ],
  [StatusCode.LATE]: [
    StatusCode.LOSS,
    StatusCode.COLLECTED,
    StatusCode.BANK,
    StatusCode.COLLECTED_EMPTY,
    StatusCode.COLLECTED_OTHER,
  ],
  [StatusCode.COLLECTED]: [
    StatusCode.COLLECTED,
    StatusCode.BANK,
    StatusCode.COLLECTED_EMPTY,
    StatusCode.COLLECTED_OTHER,
    StatusCode.LOSS,
    StatusCode.LATE,
  ],
  [StatusCode.BANK]: [
    StatusCode.COLLECTED,
    StatusCode.BANK,
    StatusCode.COLLECTED_EMPTY,
    StatusCode.COLLECTED_OTHER,
    StatusCode.LOSS,
    StatusCode.LATE,
  ],
  [StatusCode.COLLECTED_EMPTY]: [
    StatusCode.COLLECTED,
    StatusCode.BANK,
    StatusCode.COLLECTED_EMPTY,
    StatusCode.COLLECTED_OTHER,
    StatusCode.LOSS,
    StatusCode.LATE,
  ],
  [StatusCode.COLLECTED_OTHER]: [
    StatusCode.COLLECTED,
    StatusCode.BANK,
    StatusCode.COLLECTED_EMPTY,
    StatusCode.COLLECTED_OTHER,
    StatusCode.LOSS,
    StatusCode.LATE,
  ],
};

export function isFlagbagServerStatus(value: unknown): value is StatusCode {
  return Object.values(FlagbagServerStatus).includes(value as StatusCode);
}

export function normalizeFlagbagServerStatus(value: unknown): StatusCode {
  if (isFlagbagServerStatus(value)) {
    return value;
  }

  switch (String(value ?? "").trim()) {
    case "collected_bank":
    case "collectedSubToBank":
      return StatusCode.BANK;
    case "collectedEmpty":
      return StatusCode.COLLECTED_EMPTY;
    case "collectedOther":
      return StatusCode.COLLECTED_OTHER;
    case "distributedOther":
      return StatusCode.DISTRIBUTED_OTHER;
    case "reserved":
      return StatusCode.NOT_ASSIGN;
    default:
      return StatusCode.NOT_ASSIGN;
  }
}

/** Allowed target statuses for a given current status. */
export function getAllowedStatuses(currentStatus: StatusCode): StatusCode[] {
  return ALLOWED_STATUS_MAP[currentStatus] ?? [];
}

export function isCollectedServerStatus(status: StatusCode): boolean {
  return COLLECTED_SERVER_STATUSES.includes(status);
}

export function convertApiStatusToStatusCode(apiStatus: string): StatusCode {
  const converted = normalizeFlagbagServerStatus(apiStatus);
  if (converted === StatusCode.NOT_ASSIGN && apiStatus && apiStatus !== StatusCode.NOT_ASSIGN && apiStatus !== "reserved") {
    console.warn("Unknown API status:", apiStatus, "using NOT_ASSIGN as default");
  }
  return converted;
}

export function convertStatusCodeToValue(apiCode: string): string {
  const normalized = normalizeFlagbagServerStatus(apiCode);
  const valueMap: Record<StatusCode, string> = {
    [StatusCode.NOT_ASSIGN]: "NOT_ASSIGN",
    [StatusCode.ASSIGNED]: "ASSIGNED",
    [StatusCode.DISTRIBUTED]: "DISTRIBUTED",
    [StatusCode.DISTRIBUTED_OTHER]: "DISTRIBUTED_OTHER",
    [StatusCode.COLLECTED]: "COLLECTED",
    [StatusCode.BANK]: "BANK",
    [StatusCode.COLLECTED_EMPTY]: "COLLECTED_EMPTY",
    [StatusCode.COLLECTED_OTHER]: "COLLECTED_OTHER",
    [StatusCode.LOSS]: "LOSS",
    [StatusCode.LATE]: "LATE",
  };
  return valueMap[normalized];
}

export function convertStatusCodeToApiCode(statusCode: StatusCode): string {
  return statusCode;
}

export function getComingStatusListLocal(status: StatusCode): StatusCode[] {
  switch (status) {
    case StatusCode.NOT_ASSIGN:
      return [StatusCode.ASSIGNED];
    case StatusCode.ASSIGNED:
      return [StatusCode.DISTRIBUTED];
    case StatusCode.DISTRIBUTED_OTHER:
    case StatusCode.DISTRIBUTED:
    case StatusCode.COLLECTED:
    case StatusCode.BANK:
    case StatusCode.COLLECTED_EMPTY:
    case StatusCode.COLLECTED_OTHER:
    case StatusCode.LOSS:
    case StatusCode.LATE:
      return [
        StatusCode.COLLECTED_EMPTY,
        StatusCode.COLLECTED,
        StatusCode.COLLECTED_OTHER,
        StatusCode.BANK,
      ];
    default:
      return [];
  }
}

export function convertComingStatusListLocalToServerStatusList(
  statusList: StatusCode[],
): StatusCode[] {
  return [...statusList];
}

export function matchesFlagbagFilterStatus(
  status: StatusCode,
  filterStatus: FlagbagFilterStatus,
): boolean {
  switch (filterStatus) {
    case FlagbagFilterStatus.ALL:
      return true;
    case FlagbagFilterStatus.RECEIVED_TOTAL:
      return COLLECTED_SERVER_STATUSES.includes(status);
    case FlagbagFilterStatus.NOT_RECEIVED_TOTAL:
      return NOT_RECEIVED_SERVER_STATUSES.includes(status);
    case FlagbagFilterStatus.RESERVED:
    case FlagbagFilterStatus.EXTRA:
    case FlagbagFilterStatus.LESS:
      return false;
    default:
      return status === (filterStatus as unknown as StatusCode);
  }
}

export function isReservedFlagbag(flagbag?: {
  bagType?: string;
  status?: unknown;
  currentStatus?: unknown;
} | null): boolean {
  if (!flagbag) return false;
  const effectiveStatus = normalizeFlagbagServerStatus(
    flagbag.currentStatus ?? flagbag.status,
  );
  return flagbag.bagType === "spare" && effectiveStatus === StatusCode.NOT_ASSIGN;
}
